import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { type EligibilityRule } from '../eligibility-setup/eligibility-setup';
import { DataService } from '../../services/data.service';

export type CriterionStatus = 'pass' | 'fail' | 'warn' | 'na';

export interface CriterionResult {
  label: string;
  icon: string;
  status: CriterionStatus;
  detail: string;
  applicantValue: string;
  requiredValue: string;
}

export interface CheckResult {
  eligible: boolean;
  totalCriteria: number;
  passed: number;
  failed: number;
  warned: number;
  criteria: CriterionResult[];
  ruleName: string;
  programClass: string;
}

interface SubjectEntry {
  name: string;
  marksObtained: number | null;
  maxMarks: number | null;
}

interface DocumentRequirement {
  name: string;
  mandatory: boolean;
}


@Component({
  selector: 'app-eligibility-check',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './eligibility-check.html',
  styleUrl: './eligibility-check.scss',
})
export class EligibilityCheck {
  submitted = signal(false);
  checkDone = signal(false);
  checkResult = signal<CheckResult | null>(null);
  printing = false;
  searchTerm: string = '857669';

  // Student input form
  student = {
    name: '',
    dateOfBirth: '',
    gender: '',
    category: '',
    nationality: 'Indian',
    aadhaarNumber: '',
    previousSchool: '',
    board: '',
    passingYear: '',
    percentage: null as number | null,
    entranceScore: null as number | null,
    hasDomicile: false,
    hasMedicalFitness: false,
  };

  subjects: SubjectEntry[] = [
    { name: '', marksObtained: null, maxMarks: 100 },
  ];

  uploadedDocNames: string[] = [];

  selectedRuleId: number | string = '';

  readonly genderOptions = ['Male', 'Female', 'Other'];
  readonly categoryOptions = ['General', 'OBC', 'SC', 'ST', 'EWS', 'PH', 'Sports', 'NRI'];
  boardOptions: any = [];

  readonly passingYears: number[] = (() => {
    const y = [];
    for (let i = new Date().getFullYear(); i >= 2000; i--) y.push(i);
    return y;
  })();

  // Seeded rules (mirrors the setup defaults — in a real app this would be a shared service)
  // rules: EligibilityRule[] = [];
  rules = signal<EligibilityRule[]>([]);

  loading = false;
  allCategories: any = [];
  defaultDocuments: DocumentRequirement[] = [];
  form: any = [];

  constructor(public dataService: DataService) {
    //
  }

  ngOnInit() {
    let stdData: any = localStorage.getItem("data4chk");
    if(stdData) {
      stdData = JSON.parse(stdData);
      this.student = {
        name: stdData.name,
        dateOfBirth: stdData.dob.substr(0,10) || '',
        gender: stdData.gender=='M' ? 'Male' : 'Female',
        nationality: stdData.country || 'Indian',
        previousSchool: stdData.pass_from,
        passingYear: stdData.pass_year,
        percentage: stdData.pass_marks || 0,
        category: '',
        aadhaarNumber: '',
        board: '',
        entranceScore: null as number | null,
        hasDomicile: false,
        hasMedicalFitness: false
      };
    }

    this.boardOptions = this.dataService.boards;
    this.allCategories = this.dataService.categories;
    const categorySeats: any = this.allCategories.slice(0, 1).map((cat: any) => ({
      category: cat,
      totalSeats: null,
      reservedPct: null,
    }));

    this.loading = true;
    this.dataService.getAllRecords('erules').then((res: any) => {
      if(res) {
        for(let i=0; i<res.length; i++) {
          res[i].subject_marks = res[i].subject_marks?.map((item: any) => JSON.parse(item));
          res[i].category_seats = res[i].category_seats?.map((item: any) => JSON.parse(item));
          res[i].docs_req = res[i].docs_req?.map((item: any) => JSON.parse(item));
          let prog: any = res[i].eprogram?.split("-");

          this.form.id = res[i].id;
          this.form.ruleName = res[i].rule_name;
          this.form.programClass = prog[0];
          this.form.stream = prog[1]=='na' ? '' : prog[1];
          this.form.academicYear = res[i].academic_year;
          this.form.status = res[i].status;
          this.form.remarks = res[i].remarks;
          this.form.minAge = res[i].min_age_years;
          this.form.maxAge = res[i].max_age_years;
          this.form.ageAsOnDate = res[i].age_date;
          this.form.minPercentage = res[i].min_percentage;
          this.form.graceMarks = res[i].grace_marks;
          this.form.requiredBoard = res[i].req_board;
          this.form.passingYearFrom = res[i].passing_year_from;
          this.form.passingYearTo = res[i].passing_year_to;
          this.form.entranceRequired = res[i].entrance_required;
          this.form.entranceExamName = res[i].entrance_exam_name;
          this.form.minEntranceScore = res[i].min_entrance_score;
          this.form.subjectRequirements = res[i].subject_marks || [{}];
          this.form.categorySeats = res[i].category_seats || categorySeats;
          this.form.documents = res[i].docs_req || this.defaultDocuments.map((d: any) => ({ ...d }))
        
          // insert in the existing data for screen
          const newRule: EligibilityRule = { ...this.form };
          this.rules.update(list => [...list, newRule]);
        }
      }
      this.loading = false;
    });

  }

  get activeRules(): EligibilityRule[] {
    return this.rules().filter(r => r.status === 'Active' || r.status === 'Draft');
  }

  readonly toTotalSeats = (sum: number, c: { totalSeats: number | null }) =>
    sum + (c.totalSeats ?? 0);

  get selectedRule(): EligibilityRule | undefined {
    return this.rules().find(r => r.id === Number(this.selectedRuleId));
  }

  addSubject(): void {
    this.subjects.push({ name: '', marksObtained: null, maxMarks: 100 });
  }

  removeSubject(i: number): void {
    if (this.subjects.length > 1) this.subjects.splice(i, 1);
  }

  onDocFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.uploadedDocNames = Array.from(input.files).map(f => f.name);
    }
  }

  computeAge(dob: string, asOnDate: string): number | null {
    if (!dob) return null;
    const d = new Date(dob);
    const ref = asOnDate ? new Date(asOnDate) : new Date();
    let age = ref.getFullYear() - d.getFullYear();
    const m = ref.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && ref.getDate() < d.getDate())) age--;
    return age;
  }

  runCheck(): void {
    this.submitted.set(true);
    if (!this.selectedRuleId || !this.student.name || !this.student.dateOfBirth ||
        !this.student.category || !this.student.board || !this.student.passingYear ||
        this.student.percentage === null) {
      return;
    }

    const rule = this.selectedRule!;
    const criteria: CriterionResult[] = [];

    // 1. AGE
    const age = this.computeAge(this.student.dateOfBirth, rule.ageAsOnDate);
    if (rule.minAge !== null || rule.maxAge !== null) {
      const minOk = rule.minAge === null || (age !== null && age >= rule.minAge);
      const maxOk = rule.maxAge === null || (age !== null && age <= rule.maxAge);
      criteria.push({
        label: 'Age Criterion',
        icon: 'bi-calendar-person-fill',
        status: (minOk && maxOk) ? 'pass' : 'fail',
        detail: (minOk && maxOk)
          ? `Applicant is ${age} years old — within allowed range.`
          : `Applicant is ${age ?? '?'} years old — outside the required ${rule.minAge}–${rule.maxAge} year range.`,
        applicantValue: age !== null ? `${age} years` : 'Not provided',
        requiredValue: `${rule.minAge ?? '—'}–${rule.maxAge ?? '—'} years`,
      });
    }

    // 2. PERCENTAGE
    const effectiveMin = (rule.minPercentage ?? 0) - (rule.graceMarks ?? 0);
    const pctOk = (this.student.percentage ?? 0) >= effectiveMin;
    criteria.push({
      label: 'Minimum Percentage',
      icon: 'bi-percent',
      status: pctOk ? 'pass' : 'fail',
      detail: pctOk
        ? `${this.student.percentage}% meets the minimum of ${rule.minPercentage}% (grace: ${rule.graceMarks ?? 0}%).`
        : `${this.student.percentage}% is below the required ${rule.minPercentage}% (after grace: ${effectiveMin}%).`,
      applicantValue: `${this.student.percentage}%`,
      requiredValue: `≥ ${rule.minPercentage}% (grace ${rule.graceMarks ?? 0}%)`,
    });

    // 3. BOARD
    if (rule.requiredBoard !== 'Any') {
      const boardOk = this.student.board === rule.requiredBoard;
      criteria.push({
        label: 'Board',
        icon: 'bi-building',
        status: boardOk ? 'pass' : 'fail',
        detail: boardOk
          ? `Board matches the required board.`
          : `Applicant's board (${this.student.board}) does not match the required board (${rule.requiredBoard}).`,
        applicantValue: this.student.board,
        requiredValue: rule.requiredBoard,
      });
    }

    // 4. PASSING YEAR
    const yr = Number(this.student.passingYear);
    const yrFrom = rule.passingYearFrom ?? 0;
    const yrTo = rule.passingYearTo ?? 9999;
    const yrOk = yr >= yrFrom && yr <= yrTo;
    criteria.push({
      label: 'Passing Year',
      icon: 'bi-calendar-check',
      status: yrOk ? 'pass' : 'fail',
      detail: yrOk
        ? `Passing year ${yr} is within the accepted range.`
        : `Passing year ${yr} is outside the accepted range (${yrFrom}–${yrTo}).`,
      applicantValue: String(yr),
      requiredValue: `${yrFrom}–${yrTo}`,
    });

    // 5. SUBJECT-WISE MARKS
    rule.subjectRequirements
      .filter(r => r.name && r.name.trim())
      .forEach(req => {
        const match = this.subjects.find(
          s => s.name.trim().toLowerCase() === req.name.trim().toLowerCase()
        );
        const obtained = match ? (match.marksObtained ?? 0) : null;
        const max = match?.maxMarks ?? 100;
        const pct = obtained !== null ? Math.round((obtained / max) * 100) : null;
        const required = req.minMarks ?? 0;
        const ok = pct !== null ? pct >= required : !req.mandatory;
        const status: CriterionStatus = pct === null
          ? (req.mandatory ? 'fail' : 'warn')
          : (ok ? 'pass' : 'fail');

        criteria.push({
          label: `Subject: ${req.name}`,
          icon: 'bi-journal-text',
          status,
          detail: pct === null
            ? `Subject not provided. ${req.mandatory ? 'This subject is mandatory.' : 'Optional subject.'}`
            : ok
              ? `${pct}% in ${req.name} meets the minimum of ${required}%.`
              : `${pct}% in ${req.name} is below the minimum of ${required}%.`,
          applicantValue: pct !== null ? `${pct}%` : 'Not provided',
          requiredValue: `≥ ${required}%${req.mandatory ? ' (mandatory)' : ' (optional)'}`,
        });
      });

    // 6. ENTRANCE EXAM
    if (rule.entranceRequired) {
      const entOk = (this.student.entranceScore ?? 0) >= (rule.minEntranceScore ?? 0);
      criteria.push({
        label: `Entrance Exam: ${rule.entranceExamName || 'Required'}`,
        icon: 'bi-pencil-square',
        status: entOk ? 'pass' : 'fail',
        detail: entOk
          ? `Score ${this.student.entranceScore} meets the minimum of ${rule.minEntranceScore}.`
          : `Score ${this.student.entranceScore ?? 'not provided'} is below the minimum of ${rule.minEntranceScore}.`,
        applicantValue: this.student.entranceScore !== null ? String(this.student.entranceScore) : 'Not provided',
        requiredValue: `≥ ${rule.minEntranceScore}`,
      });
    }

    // 7. DOMICILE
    if (rule.domicileRequired) {
      criteria.push({
        label: 'Domicile Certificate',
        icon: 'bi-geo-alt-fill',
        status: this.student.hasDomicile ? 'pass' : 'fail',
        detail: this.student.hasDomicile
          ? 'Domicile certificate is available.'
          : 'Domicile certificate is required but not provided.',
        applicantValue: this.student.hasDomicile ? 'Available' : 'Not available',
        requiredValue: 'Required',
      });
    }

    // 8. MEDICAL FITNESS
    if (rule.medicalFitness) {
      criteria.push({
        label: 'Medical Fitness',
        icon: 'bi-heart-pulse-fill',
        status: this.student.hasMedicalFitness ? 'pass' : 'warn',
        detail: this.student.hasMedicalFitness
          ? 'Medical fitness certificate available.'
          : 'Medical fitness certificate required — may be submitted at time of admission.',
        applicantValue: this.student.hasMedicalFitness ? 'Available' : 'Not yet',
        requiredValue: 'Required',
      });
    }

    // 9. CATEGORY SEAT
    const seatRow = rule.categorySeats.find(c => c.category === this.student.category);
    if (seatRow) {
      criteria.push({
        label: `Category Seat (${this.student.category})`,
        icon: 'bi-people-fill',
        status: (seatRow.totalSeats ?? 0) > 0 ? 'pass' : 'warn',
        detail: (seatRow.totalSeats ?? 0) > 0
          ? `${seatRow.totalSeats} seats available in ${this.student.category} category.`
          : `No seats allocated for ${this.student.category} category in this rule.`,
        applicantValue: this.student.category,
        requiredValue: `${seatRow.totalSeats ?? 0} seats`,
      });
    }

    const failed = criteria.filter(c => c.status === 'fail').length;
    const warned = criteria.filter(c => c.status === 'warn').length;
    const passed = criteria.filter(c => c.status === 'pass').length;

    this.checkResult.set({
      eligible: failed === 0,
      totalCriteria: criteria.length,
      passed,
      failed,
      warned,
      criteria,
      ruleName: rule.ruleName,
      programClass: rule.programClass,
    });
    this.checkDone.set(true);

    setTimeout(() => {
      document.getElementById('check-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }

  resetCheck(): void {
    this.submitted.set(false);
    this.checkDone.set(false);
    this.checkResult.set(null);
    this.selectedRuleId = '';
    this.student = {
      name: '', dateOfBirth: '', gender: '', category: '', nationality: 'Indian',
      aadhaarNumber: '', previousSchool: '', board: '', passingYear: '',
      percentage: null, entranceScore: null, hasDomicile: false, hasMedicalFitness: false,
    };
    this.subjects = [{ name: '', marksObtained: null, maxMarks: 100 }];
    this.uploadedDocNames = [];
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getStatusIcon(status: CriterionStatus): string {
    return {
      pass: 'bi-check-circle-fill',
      fail: 'bi-x-circle-fill',
      warn: 'bi-exclamation-triangle-fill',
      na: 'bi-dash-circle',
    }[status];
  }

  getStatusColor(status: CriterionStatus): string {
    return { pass: '#22c55e', fail: '#ef4444', warn: '#f59e0b', na: '#9ca3af' }[status];
  }

  printResult(): void {
    window.print();
  }

  getEditData(frmData: any) {
    if(frmData.std_info && typeof frmData.std_info == 'string') {
      frmData.std_info = JSON.parse(frmData.std_info);
    }
    
    if(frmData.f_info && typeof frmData.f_info == 'string') {
      frmData.f_info = JSON.parse(frmData.f_info);
    }

    if(frmData.m_info && typeof frmData.m_info == 'string') {
      frmData.m_info = JSON.parse(frmData.m_info);
    }

    if(frmData.g_info && typeof frmData.g_info == 'string') {
      frmData.g_info = JSON.parse(frmData.g_info);
    }

    if(frmData.additional && typeof frmData.additional == 'string') {
      frmData.additional = JSON.parse(frmData.additional);
    }

    if(frmData.declaration && typeof frmData.declaration == 'string') {
      frmData.declaration = JSON.parse(frmData.declaration);
    }

    if(frmData.academics && typeof frmData.academics == 'string') {
      frmData.academics = JSON.parse(frmData.academics);
    }

    if(frmData.subjects && typeof frmData.subjects == 'string') {
      frmData.subjects = JSON.parse(frmData.subjects);
    }

    if(frmData.addresses && typeof frmData.addresses == 'string') {
      frmData.addresses = this.dataService.str2json(frmData.addresses);
    }
    
    if(frmData.documents && typeof frmData.documents == 'string') {
      frmData.documents = this.dataService.str2json(frmData.documents);
    }

    this.form.id = frmData.id;
    const stddob: string = frmData.std_dob?.substr(0,10);
    this.student = {
      name: frmData.std_name,
      dateOfBirth: stddob,
      gender: frmData.std_info.gender,
      category: frmData.std_info.category,
      nationality: 'Indian',
      aadhaarNumber: frmData.std_info.aadhaarNumber,
      previousSchool: frmData.academics.previousSchool,
      board: frmData.academics.board,
      passingYear: frmData.academics.passingYear,
      percentage: frmData.academics.percentage,
      entranceScore: null as number | null,
      hasDomicile: false,
      hasMedicalFitness: false,
    };
  }

  onSearchChange(): void {
    this.dataService.getSingleRecord('applications', this.searchTerm).then((res: any) => {
      if(res) {
        this.getEditData(res);
      } else {
        //
      }
    });
  }

  updateStatus() {
    this.loading = true;
    const currentDate: Date = new Date();
    let result = this.checkResult()!;
    const myid = this.form.id;
    const frmData: any = {
      'is_active': false, 
      'is_eligible': result.eligible ? 'Yes' : 'No', 
      'status_date': currentDate
    };
    this.dataService.updateRecord('appli_status', myid, frmData, true).subscribe({
      next: (response) => {
        // console.log('Response received in component:', response);
        this.loading = false;
        this.resetCheck();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }
}
